import { ClassNameMap, Dialog, DialogClassKey, Fab, FabClassKey, SvgIconClassKey } from '@mui/material';
import React, { PropsWithChildren, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { CloseButton } from '../../buttons';

const useStylesStandard = makeStyles({ name: 'MobileTooltipStandard' })(theme => ({
	paper: {
		boxSizing: 'border-box',
		backgroundColor: theme.palette.tertiary.main,
		color: '#ffffff',
		width: '100%',
		maxWidth: 'unset',
		maxHeight: '70%',
		margin: 0,
		borderRadius: 0,
		marginTop: 'auto',
		textAlign: 'center',
		padding: 40
	},
	buttonWrapper: {
		marginTop: 'auto',
		paddingTop: 36,
		textAlign: 'center'
	},
	buttonCloseRoot: {
		marginLeft: 'auto',
		marginRight: 'auto',
		color: '#ffffff',
		border: '1px solid rgba(255,255,255,0.2)',
		'&:hover': {
			borderColor: theme.palette.skodaColors['neutrals-white']
		},
		'&:not(.btn)': {
			color: '#ffffff'
		},
		'&:not(.btn):hover': {
			color: '#ffffff'
		}
	},
	closeButtonExtended: {
		display: 'none'
	}
}));

const useStylesExtended = makeStyles({ name: 'MobileTooltipExtended' })(theme => ({
	paper: {
		boxSizing: 'border-box',
		backgroundColor: '#f3f3f3',
		width: '100%',
		maxWidth: 'unset',
		height: '100%',
		maxHeight: 'unset',
		margin: 0,
		borderRadius: 0,
		padding: 40
	},
	buttonWrapper: {
		marginTop: 'auto',
		paddingTop: 36,
		textAlign: 'center'
	},
	buttonCloseRoot: {
		marginLeft: 'auto',
		marginRight: 'auto'
	},
	closeButtonExtended: {
		position: 'absolute',
		alignSelf: 'flex-end'
	}
}));

export type MobileTooltipVariant = 'standard' | 'extended';

export type MobileTooltipClasses = {
	dialogClasses?: Partial<ClassNameMap<DialogClassKey>>,
	buttonCloseClasses?: Partial<ClassNameMap<FabClassKey>>,
	iconClasses?: Partial<ClassNameMap<SvgIconClassKey>>
};

export type MobileTooltipProps<AnchorProps> = {
	onOpen?: () => void;
	onClose?: () => void;
	AnchorComponent: React.ComponentType<AnchorProps>;
	AnchorComponentProps?: AnchorProps;
	textClose: string;
	variant?: MobileTooltipVariant;
	classes?: MobileTooltipClasses;
};

export function MobileTooltip<AnchorProps>(props: PropsWithChildren<MobileTooltipProps<AnchorProps>>) {
	const { onOpen, onClose, AnchorComponent, AnchorComponentProps, classes, textClose, variant, children } = props;
	const [isOpen, setIsOpen] = useState(false);

	let { classes: stylesStandard, cx } = useStylesStandard();
	let { classes: stylesExtended } = useStylesExtended();

	const styles = variant === 'standard' ? stylesStandard : stylesExtended;

	const handleOpen = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
		e.preventDefault();
		setIsOpen(true);
		onOpen?.();
	};

	const handleClose = () => {
		setIsOpen(false);
		onClose?.();
	};

	const dialogClasses = {
		...classes?.dialogClasses,
		paper: cx(styles.paper, classes?.dialogClasses?.paper)
	};

	const buttonCloseClasses = {
		...classes?.buttonCloseClasses,
		root: cx(styles.buttonCloseRoot, classes?.buttonCloseClasses?.root)
	};

	return (
		<React.Fragment>
			<AnchorComponent
				{...AnchorComponentProps}
				onClick={(e) => handleOpen(e)} />
			<Dialog
				onClick={e => e.stopPropagation()}
				fullWidth
				classes={dialogClasses}
				open={isOpen}
				onBackdropClick={handleClose}>
				<CloseButton
					className={styles.closeButtonExtended}
					onClick={handleClose} />
				{children}
				<div className={styles.buttonWrapper}>
					<Fab
						classes={buttonCloseClasses}
						variant='extended'
						color='secondary'
						onClick={handleClose}>
						{textClose}
					</Fab>
				</div>
			</Dialog>
		</React.Fragment>
	);
}

MobileTooltip.defaultProps = {
	variant: 'standard'
};
